import { Linkify } from "./Linkify";
import { StatusDot } from "./StatusDot";

export type FeedbackThreadMessage = {
  id: string;
  author: "admin" | "user";
  /** How the message arrived. 'email' earns a quiet "via email" mark. */
  channel: "web" | "email";
  body: string;
  emailStatus: string | null;
  createdAt: number;
  /** Pre-formatted display string for `createdAt` — see `FeedbackThread`'s
   *  doc for why this component never formats the raw epoch itself. */
  displayCreatedAt: string;
};

export type FeedbackThreadHead = {
  body: string;
  createdAt: number;
  /** Pre-formatted display string for `createdAt` — see `FeedbackThread`'s
   *  doc for why this component never formats the raw epoch itself. */
  displayCreatedAt: string;
  authorLabel: string;
};

type EmailMark = { text: string; className: string };

// No unicode marks (globals.css's rule): the words already say the state, and
// the drawn coral problem dot leading `.fb-email-status.failed` is the state
// cue for delivery failure.
function emailMarkFor(status: string | null): EmailMark {
  if (status === "sent") return { text: "Sent", className: "" };
  if (status === "failed") return { text: "Failed", className: "failed" };
  if (status === "skipped") return { text: "Skipped", className: "" };
  return { text: "pre-tracking", className: "pretracking" };
}

function Bubble({
  side,
  label,
  body,
  displayCreatedAt,
  mark,
  viaEmail = false,
}: {
  side: "admin" | "user";
  label?: string;
  body: string;
  displayCreatedAt: string;
  mark?: EmailMark | null;
  viaEmail?: boolean;
}) {
  return (
    <div className={`fb-bubble ${side}`}>
      {label && <span className="fb-bubble-label">{label}</span>}
      <p className="fb-bubble-body">
        <Linkify text={body} />
      </p>
      <div className="fb-bubble-meta">
        <span>{displayCreatedAt}</span>
        {viaEmail && <span className="fb-via-email">via email</span>}
        {mark && (
          <span className={`fb-email-status ${mark.className}`}>
            {mark.className === "failed" && <StatusDot tone="problem" />}
            {mark.text}
          </span>
        )}
      </div>
    </div>
  );
}

/**
 * Renders a feedback conversation: the original submission as a head bubble,
 * then every reply in order. Shared between the admin triage row (with email
 * delivery marks) and the user-facing widget (read-only, no marks).
 *
 * Every timestamp renders from its sibling `displayCreatedAt` string, never
 * from `createdAt` directly. The admin triage row renders this from a Server
 * Component, so a render-time `toLocaleString()` would run once during SSR
 * (UTC on Vercel) and again in the browser (Eastern) and produce two strings
 * for one message — React's hydration error #418. The admin page formats
 * server-side with `formatDateTime` (lib/format.ts); the widget formats its
 * own client-fetched list through the same function, so both sides of a
 * thread read the same string.
 */
export function FeedbackThread({
  head,
  messages,
  showEmailStatus,
}: {
  head: FeedbackThreadHead;
  messages: FeedbackThreadMessage[];
  showEmailStatus: boolean;
}) {
  return (
    <div className="fb-thread">
      <Bubble side="user" label={head.authorLabel} body={head.body} displayCreatedAt={head.displayCreatedAt} />
      {messages.map((m) => (
        <Bubble
          key={m.id}
          side={m.author === "admin" ? "admin" : "user"}
          body={m.body}
          displayCreatedAt={m.displayCreatedAt}
          viaEmail={m.channel === "email"}
          mark={showEmailStatus && m.author === "admin" ? emailMarkFor(m.emailStatus) : null}
        />
      ))}
    </div>
  );
}
